import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IonButton, IonCheckbox, IonIcon, IonItem, IonLabel, IonList, IonNote, IonSpinner, IonText, useIonAlert,
} from '@ionic/react';
import { caretDown } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';
import { createGroupedInvoice, type GroupedInvoiceResult } from '../services/zohoServiceOrderService';
import PortalDropdown from './PortalDropdown';
import styles from './GroupedInvoiceCard.module.scss';

interface Props {
  /** The customer's Zoho Books contact_id (Contact → External IDs). */
  zohoContactId: string;
}

const MONTHS_BACK = 12;

/**
 * Monthly grouped invoicing for customers whose Invoicing setting is "Grouped":
 * pick a month, preview the confirmed sales orders that would go on one invoice
 * (untick any to leave it off), then create it. Everything stays in Zoho Books;
 * nothing here is saved locally.
 */
const GroupedInvoiceCard: React.FC<Props> = ({ zohoContactId }) => {
  const { t } = useTranslation();
  const [presentAlert] = useIonAlert();

  const monthOptions = useMemo(() => {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat(intlLocale(), { month: 'long', year: 'numeric' });
    return Array.from({ length: MONTHS_BACK }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: fmt.format(d) };
    });
  }, []);

  // Default to last month: that is the month normally being billed.
  const [month, setMonth] = useState(monthOptions[1]?.value ?? monthOptions[0].value);
  const [monthOpen, setMonthOpen] = useState(false);
  const monthTriggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  // The dropdown's portal root must live OUTSIDE this card: the glass IonCard (overflow:hidden +
  // backdrop-filter) both clips a dropdown and turns position:fixed into card-relative. Host it on
  // the enclosing modal / page instead, like BookingsListModal's picker root.
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const anchor = monthTriggerRef.current;
    setPortalHost(anchor?.closest<HTMLElement>('ion-modal') ?? anchor?.closest<HTMLElement>('.ion-page') ?? document.body);
  }, []);

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<GroupedInvoiceResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<GroupedInvoiceResult | null>(null);

  const monthLabel = monthOptions.find(o => o.value === month)?.label ?? month;

  const chooseMonth = (value: string) => {
    setMonth(value);
    setPreview(null);
    setSelected(new Set());
    setCreated(null);
  };

  const showError = (error: unknown) => {
    console.error('Grouped invoice failed:', error);
    presentAlert({
      header: t('components.GroupedInvoiceCard.failed'),
      message: error instanceof Error ? error.message : t('components.GroupedInvoiceCard.tryAgain'),
      cssClass: 'glass-alert',
      buttons: ['OK'],
    });
  };

  const handlePreview = async () => {
    setBusy(true);
    setCreated(null);
    try {
      const result = await createGroupedInvoice(zohoContactId, month, true);
      setPreview(result);
      setSelected(new Set(result.salesOrders.map(so => so.salesorder_id)));
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const selectedOrders = preview?.salesOrders.filter(so => selected.has(so.salesorder_id)) ?? [];
  const selectedTotal = selectedOrders.reduce((sum, so) => sum + so.total, 0);

  const createNow = async () => {
    setBusy(true);
    try {
      setCreated(await createGroupedInvoice(zohoContactId, month, false, selectedOrders.map(so => so.salesorder_id)));
      setPreview(null);
      setSelected(new Set());
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    if (!selectedOrders.length) return;
    presentAlert({
      header: t('components.GroupedInvoiceCard.createInvoice'),
      message: t('components.GroupedInvoiceCard.confirm', { count: selectedOrders.length }),
      cssClass: 'glass-alert',
      buttons: [
        { text: t('components.GroupedInvoiceCard.cancel'), role: 'cancel' },
        { text: t('components.GroupedInvoiceCard.create'), handler: () => { void createNow(); } },
      ],
    });
  };

  if (!zohoContactId) {
    return (
      <IonItem lines="none" className={styles.row}>
        <IonNote className={styles.note}>{t('components.GroupedInvoiceCard.noZohoContact')}</IonNote>
      </IonItem>
    );
  }

  return (
    <IonList lines="none" className={styles.list}>
      {portalHost && createPortal(<div ref={portalRef} className={styles.portalRoot} />, portalHost)}

      {/* Month selector: same trigger as Client History's "Last 30 days" */}
      <IonItem lines="none" className={styles.row}>
        <IonLabel>
          <IonText className={styles.monthTitle}>{t('components.GroupedInvoiceCard.title')}</IonText>
          <button
            ref={monthTriggerRef}
            type="button"
            className={`${styles.monthButton} ${monthOpen ? styles.isOpen : ''}`}
            disabled={busy}
            onClick={() => setMonthOpen(o => !o)}
          >
            {monthLabel} <IonIcon icon={caretDown} size="small" />
          </button>
        </IonLabel>
        <IonButton slot="end" fill="solid" size="small" onClick={handlePreview} disabled={busy}>
          {busy && !preview ? <IonSpinner name="dots" /> : t('components.GroupedInvoiceCard.preview')}
        </IonButton>
      </IonItem>
      <PortalDropdown
        isOpen={monthOpen}
        anchorRef={monthTriggerRef}
        portalRef={portalRef}
        onClose={() => setMonthOpen(false)}
        align="start"
        matchWidth={false}
        options={monthOptions.map(o => ({ label: o.label, value: o.value, selected: o.value === month }))}
        onSelect={chooseMonth}
      />

      {preview && (
        <>
          {preview.salesOrders.length === 0 ? (
            <IonItem lines="none" className={styles.row}><IonNote className={styles.note}>{t('components.GroupedInvoiceCard.none')}</IonNote></IonItem>
          ) : (
            <>
              {preview.salesOrders.map(so => (
                <IonItem
                  key={so.salesorder_id}
                  lines="none"
                  className={`${styles.row} ${selected.has(so.salesorder_id) ? '' : styles.skippedOrder}`}
                >
                  <IonCheckbox
                    slot="start"
                    checked={selected.has(so.salesorder_id)}
                    disabled={busy}
                    aria-label={so.salesorder_number}
                    onIonChange={e => toggle(so.salesorder_id, e.detail.checked)}
                  />
                  <IonLabel>
                    <h3>{so.salesorder_number}</h3>
                    <p>{so.date}</p>
                  </IonLabel>
                  <IonText slot="end">{so.total.toFixed(2)}</IonText>
                </IonItem>
              ))}
              <IonItem lines="none" className={styles.row}>
                <IonLabel>{t('components.GroupedInvoiceCard.total')}</IonLabel>
                <IonText slot="end">{selectedTotal.toFixed(2)}</IonText>
              </IonItem>
              <IonItem lines="none" className={styles.row}>
                <IonButton slot="end" fill="solid" size="small" onClick={handleCreate} disabled={busy || selectedOrders.length === 0}>
                  {busy ? <IonSpinner name="dots" /> : t('components.GroupedInvoiceCard.createInvoice')}
                </IonButton>
              </IonItem>
            </>
          )}
          {preview.skipped.length > 0 && (
            <IonItem lines="none" className={styles.row}>
              <IonNote className={styles.note}>{t('components.GroupedInvoiceCard.skipped', { count: preview.skipped.length })}</IonNote>
            </IonItem>
          )}
        </>
      )}

      {created && (
        <IonItem lines="none" className={styles.row}>
          <IonLabel className="ion-text-wrap">
            <h3>{t('components.GroupedInvoiceCard.created', { number: created.invoiceNumber })}</h3>
            <p>{t('components.GroupedInvoiceCard.linked', { count: created.linkedSalesOrders?.filter(o => o.status === 'invoiced').length ?? 0, total: created.salesOrders.length })}</p>
          </IonLabel>
        </IonItem>
      )}
    </IonList>
  );
};

export default GroupedInvoiceCard;
